import * as React from "react";

/**
 * useExclusiveDetails — يجعل مجموعة `<details>` تعمل بسلوك أكورديون:
 *   1. فتح عنصر يُغلق البقيّة (single-open).
 *   2. الضغط خارج الحاوية يُغلقها كلّها.
 *   3. Escape يُغلقها كلّها.
 *
 * الاستخدام: اربط `containerRef` بـ<div> يلفّ كلّ الـ<details>.
 */
export function useExclusiveDetails<T extends HTMLElement = HTMLDivElement>() {
  const containerRef = React.useRef<T | null>(null);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const allDetails = () =>
      Array.from(container.querySelectorAll<HTMLDetailsElement>("details"));

    const closeAll = () => {
      for (const d of allDetails()) if (d.open) d.open = false;
    };

    // (1) Single-open داخل المجموعة
    const onToggle = (e: Event) => {
      const target = e.target as HTMLDetailsElement | null;
      if (!target || target.tagName !== "DETAILS" || !target.open) return;
      for (const d of allDetails()) if (d !== target && d.open) d.open = false;
    };
    // toggle لا يصعد عادة → نستمع capture
    container.addEventListener("toggle", onToggle, true);

    // (2) الضغط خارج الحاوية يُغلق
    const onPointerDown = (e: PointerEvent) => {
      const path = e.composedPath();
      if (!path.includes(container)) closeAll();
    };
    document.addEventListener("pointerdown", onPointerDown);

    // (3) Escape
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      container.removeEventListener("toggle", onToggle, true);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return containerRef;
}
